import { Edit2, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

interface InlineEditTextProps {
  value: string | null;
  onSave: (value: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  displayClassName?: string;
}

const InlineEditText: React.FC<InlineEditTextProps> = ({
  value,
  onSave,
  disabled = false,
  placeholder = 'Click to edit...',
  multiline = false,
  className = '',
  displayClassName = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  const handleSave = async () => {
    if (editValue === value || disabled) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && !multiline && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div className={`space-y-2 ${className}`}>
        {multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            placeholder={placeholder}
            rows={6}
            className="w-full"
            autoFocus
          />
        ) : (
          <Input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            placeholder={placeholder}
            className="w-full"
            autoFocus
          />
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => !disabled && setIsEditing(true)}
      disabled={disabled || saving}
      className={`group text-left w-full p-2 rounded hover:bg-muted transition-colors ${displayClassName}`}
    >
      {saving ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="flex-1">
            {value ? (
              <div className={multiline ? 'whitespace-pre-wrap text-sm' : 'text-sm'}>
                {value}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">{placeholder}</div>
            )}
          </div>
          {!disabled && (
            <Edit2 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
          )}
        </div>
      )}
    </button>
  );
};

export default InlineEditText;

